import { createContext, useContext, type ReactNode } from "react";
import type { LoginTheme } from "./tokens";

// Customization surface for the login widget. ALL of this is data-only —
// no callbacks, no functions — so an agent generating customization config
// has no avenue to inject behavior. Logic (API URLs, validation rules,
// field order, flow control) is NOT exposed here by design.

export type { LoginTheme } from "./tokens";

export interface LoginText {
  title?: string;
  phoneLabel?: string;
  phoneRequired?: string;
  phoneInvalid?: string;
  codeLabel?: string;
  codePlaceholder?: string;
  codeRequired?: string;
  codeInvalid?: string;
  sendCodeText?: string;
  resendTextTemplate?: string; // e.g. "Resend in {seconds}"
  voicePromptText?: string;
  voiceCtaText?: string;
  submitText?: string;
  agreementHTML?: string; // raw HTML, host-page authored
  // Complete (loyalty profile) modal copy — opens after first-time login if
  // the member doesn't have a name on file yet.
  completeTitle?: string;
  completeJoinWithText?: string; // prefix shown before the phone number
  firstNameLabel?: string;
  firstNamePlaceholder?: string;
  lastNameLabel?: string;
  lastNamePlaceholder?: string;
  emailLabel?: string;
  emailPlaceholder?: string;
  invalidNameMessage?: string;
  illegalCharacterMessage?: string;
  invalidEmailMessage?: string;
  allowSmsText?: string;
  completeSubmitText?: string;
}

export interface LoginCustomization {
  /** Brand theme — pass `primary` and (optionally) `background`. The
   *  widget derives every other color from these and applies them as
   *  CSS variables on `:root`. */
  theme?: LoginTheme;
  /** Override copy text. All keys optional; missing keys fall back to
   *  English defaults. Renamed from the older `copy`. */
  text?: LoginText;
  /** Custom class added to the login modal root, for power-user
   *  CSS overrides via `data-peblla-part="..."` selectors. */
  className?: string;
  /** Direct antd ConfigProvider tokens. Most tenants don't need this —
   *  the widget already derives `colorPrimary`/`colorBgContainer` etc.
   *  from `theme`. Use only for fine antd token control. */
  antdToken?: Record<string, unknown>;
}

const LoginCustomContext = createContext<LoginCustomization>({});

export const LoginCustomProvider = ({
  value,
  children,
}: {
  value: LoginCustomization;
  children: ReactNode;
}) => (
  <LoginCustomContext.Provider value={value}>
    {children}
  </LoginCustomContext.Provider>
);

export const useLoginCustom = (): LoginCustomization =>
  useContext(LoginCustomContext);

export const useText = (): LoginText => {
  const { text } = useLoginCustom();
  return text ?? {};
};

export const formatResend = (template: string | undefined, seconds: number) => {
  const t = template ?? "Resend in {seconds}";
  return t.replace("{seconds}", String(seconds));
};
